import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { auth, streaming as reduxStreaming, toast } from "../../redux";
import LoginView from "./views/LoginView";
import {
  useRedirect,
  configurator,
  useFetchApi,
  Config,
  useQueryParams,
  Loading,
} from "@itarget/shared";
import {
  clearSsoAttempt,
  hasSsoAttempt,
  isSsoEnabled,
  readHashFromLocation,
  redirectToSsoAuthorize,
} from "../../utils/sso";

const LoginContainer = () => {
  const { cc } = useParams();
  const { get } = useQueryParams();
  const onlyStreamingParam = get("onlyStreaming");
  const streamingAgaId = get("streaming_aga_id");
  const dispatch = useDispatch();
  const { goTo } = useRedirect();
  const [value, setValue] = useState(0);
  const isAuthenticated = useSelector((state) => state.auth.isAutenticated);
  const selectedLang = useSelector((state) => state.language.lang);
  const user = useSelector((state) => state.auth.user);
  const center_cost_id = useSelector((state) => state.auth.cc);
  const settingCommerce = useSelector((state) => state.setting.settingCommerce);
  const [newSettingCommerce, setNewSettingCommerce] = useState({});
  let selectedCenterCostId = cc || center_cost_id;

  let { current: search } = useRef(
    new URLSearchParams(document.location.search)
  );
  let state = useMemo(() => search.get("state"), [search]);
  const registerOnly = isSsoEnabled() && state === "register";
  const shouldRedirectToSso =
    isSsoEnabled() && !isAuthenticated && state !== "register";
  const ssoStarted = useRef(false);

  const { data: reRegister, fetchData: fetchReRegister } = useFetchApi(
    Config.baseURL +
      `api/${selectedLang}/person/re/register?pessoa_id=${user?.data?.id}&centro_custo_id=${selectedCenterCostId}&aplicacoes=4`
  );

  const {
    data: settingRequest,
    fetchData: fetchSetting,
    isLoading: isLoadingSettingLogin,
  } = useFetchApi(
    Config.baseURL +
      `api/${selectedLang}/setting?centro_custo_id=${selectedCenterCostId}&${configurator.LOGIN}`
  );

  const {
    data: settingCourse,
    fetchData: fetchSettingCourse,
    isLoading,
  } = useFetchApi(
    Config.baseURL +
      `api/${selectedLang}/setting-course-commerce/setting?centro_custo_id=${selectedCenterCostId}`
  );

  const redirectUser = () => {
    if (!!reRegister?.data?.[0]) {
      goTo(`me`);
      return;
    }
    goTo(
      `offers-unique/${selectedCenterCostId}${
        streamingAgaId ? `?streaming_aga_id=${streamingAgaId}` : ""
      }`
    );
  };

  let configurationToEnableRegister =
    settingRequest?.data &&
    settingRequest.data.filter((res) => res.codigo === 316).length > 0 &&
    settingRequest.data.filter((res) => res.codigo === 316)[0];
  let bottomRegister = ["N", undefined, false].includes(
    configurationToEnableRegister?.ativo
  )
    ? undefined
    : configurationToEnableRegister?.valor_referencia;

  useEffect(() => {
    const setOnlyStreaming = (isOnlyStreaming) => {
      dispatch(
        reduxStreaming.actions.setOnlyStreaming({
          isOnlyStreaming,
          onlyStreamingCenterCostId: selectedCenterCostId,
        })
      );
    };
    if (onlyStreamingParam === "true") setOnlyStreaming(true);
    if (onlyStreamingParam === "false") setOnlyStreaming(false);
  }, [onlyStreamingParam, selectedCenterCostId]);

  useEffect(() => {
    !!reRegister && redirectUser();
  }, [reRegister]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReRegister();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!shouldRedirectToSso || ssoStarted.current) {
      return;
    }
    if (hasSsoAttempt()) {
      clearSsoAttempt();
      window.location.replace("/");
      return;
    }
    ssoStarted.current = true;
    const returnPath = selectedCenterCostId
      ? `/offers-unique/${selectedCenterCostId}`
      : "/new-panel";
    const hash = readHashFromLocation(window.location);
    redirectToSsoAuthorize(returnPath, selectedCenterCostId, hash || undefined);
  }, [shouldRedirectToSso, selectedCenterCostId]);

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (shouldRedirectToSso) {
      return;
    }
    fetchSettingCourse();
    fetchSetting();
  }, [selectedCenterCostId, shouldRedirectToSso]);

  useEffect(() => {
    setNewSettingCommerce(settingCourse?.data || settingCommerce);

    if (settingCourse?.data?.texto_aba_navegador) {
      document.title = settingCourse?.data?.texto_aba_navegador;
    }

    if (settingCourse?.data?.icone_aba_navegador) {
      document.getElementById("favicon").href =
        settingCourse?.data?.icone_aba_navegador;
    }
  }, [settingCourse, settingCommerce]);

  useEffect(() => {
    if (registerOnly || state == "register") handleChange(null, 1);
    if (!registerOnly && !["S", undefined, false].includes(bottomRegister))
      handleChange(null, 0);
  }, [bottomRegister, registerOnly, state]);

  if (shouldRedirectToSso) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading text="Redirecionando para o login..." />
      </div>
    );
  }

  return (
    <LoginView
      settingCommerce={newSettingCommerce}
      isLoadingService={isLoading}
      isLoadingSettingLogin={isLoadingSettingLogin}
      center_cost_id={selectedCenterCostId}
      isAutenticated={isAuthenticated}
      setting={settingRequest}
      bottomRegister={bottomRegister}
      registerOnly={registerOnly}
      value={value}
      auth={auth}
      toast={toast}
      handleChange={handleChange}
    />
  );
};

export default LoginContainer;
